import { useEffect, useState } from 'react';

const resolveInitialState = (initialState) => (
  typeof initialState === 'function' ? initialState() : initialState
);

const getStorageApi = (storageType) => {
  if (typeof window === 'undefined') {
    return null;
  }

  return storageType === 'session' ? window.sessionStorage : window.localStorage;
};

export const readPersistentUiState = (key, initialState, options = {}) => {
  const { storage = 'local', validate } = options;
  const fallbackState = resolveInitialState(initialState);
  const storageApi = getStorageApi(storage);

  if (!key || !storageApi) {
    return fallbackState;
  }

  try {
    const rawState = storageApi.getItem(key);
    if (!rawState) {
      return fallbackState;
    }

    const parsedState = JSON.parse(rawState);
    if (typeof validate === 'function') {
      return validate(parsedState, fallbackState);
    }
    return parsedState;
  } catch (error) {
    console.error(`Failed to restore persisted UI state for ${key}:`, error);
    return fallbackState;
  }
};

const usePersistentUiState = (key, initialState, options = {}) => {
  const { storage = 'local' } = options;
  const [state, setState] = useState(() => readPersistentUiState(key, initialState, options));

  useEffect(() => {
    const storageApi = getStorageApi(storage);
    if (!key || !storageApi) {
      return;
    }

    try {
      storageApi.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.error(`Failed to persist UI state for ${key}:`, error);
    }
  }, [key, state, storage]);

  return [state, setState];
};

export default usePersistentUiState;
